/**
 * Frontend Structured Logger
 *
 * Lightweight browser logger that:
 * - Outputs structured JSON to console in dev
 * - Batches and sends warn/error logs to the backend for persistence
 * - Provides per-module child loggers via log('ModuleName')
 * - Auto-flushes on page unload
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error'

interface FrontendLogEntry {
  level: string
  category: string
  message: string
  error?: { name: string; message: string; stack?: string }
  metadata?: Record<string, unknown>
}

const LEVEL_PRIORITY: Record<LogLevel, number> = { debug: 0, info: 1, warn: 2, error: 3 }
const MIN_LEVEL: LogLevel = (import.meta.env.VITE_LOG_LEVEL as LogLevel) ?? (import.meta.env.DEV ? 'debug' : 'warn')
const FLUSH_INTERVAL = 5000
const MAX_BUFFER = 30

const buffer: FrontendLogEntry[] = []
let flushTimer: ReturnType<typeof setInterval> | null = null

async function flushBuffer(): Promise<void> {
  if (buffer.length === 0) return
  const batch: FrontendLogEntry[] = buffer.splice(0, MAX_BUFFER)
  try {
    const { apiClient } = await import('./api/client')
    await apiClient.post('/maintenance/logs/ingest', { logs: batch })
  } catch {
    // Silently fail — don't let logging break the app
    // Re-add to buffer for retry (cap to prevent memory leak)
    if (buffer.length < 100) {
      for (let i = batch.length - 1; i >= 0; i--) {
        buffer.unshift(batch[i]!)
      }
    }
  }
}

// Start periodic flush
if (typeof window !== 'undefined') {
  flushTimer = setInterval(() => { void flushBuffer() }, FLUSH_INTERVAL)

  // Flush on page unload
  window.addEventListener('beforeunload', () => {
    if (flushTimer) clearInterval(flushTimer)
    void flushBuffer()
  })
}

function shouldLog(level: LogLevel): boolean {
  return LEVEL_PRIORITY[level] >= LEVEL_PRIORITY[MIN_LEVEL]
}

function createEntry(
  level: LogLevel,
  category: string,
  message: string,
  data?: Record<string, unknown>,
): FrontendLogEntry {
  const entry: FrontendLogEntry = { level, category, message }
  if (data !== undefined && data !== null && 'error' in data && typeof data.error === 'object' && data.error !== null) {
    const e = data.error as { name?: string; message?: string; stack?: string }
    entry.error = { name: e.name ?? 'Error', message: e.message ?? 'Unknown', stack: e.stack }
    const rest: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(data)) {
      if (k !== 'error') rest[k] = v
    }
    if (Object.keys(rest).length > 0) entry.metadata = rest
  } else if (data !== undefined && data !== null && Object.keys(data).length > 0) {
    entry.metadata = data
  }
  return entry
}

export interface Logger {
  debug(message: string, data?: Record<string, unknown>): void
  info(message: string, data?: Record<string, unknown>): void
  warn(message: string, data?: Record<string, unknown>): void
  error(message: string, data?: Record<string, unknown>): void
}

/**
 * Get a structured logger for a frontend module.
 *
 * Usage:
 *   const logger = log('JobsPage')
 *   logger.info('Loaded jobs', { count: 42 })
 *   logger.error('Failed to save', { error: err })
 */
export function log(category: string): Logger {
  return {
    debug(message: string, data?: Record<string, unknown>) {
      if (!shouldLog('debug')) return
      if (import.meta.env.DEV) {
        console.warn(`[DEBUG][${category}]`, message, data ?? '') // dev-only
      }
    },
    info(message: string, data?: Record<string, unknown>) {
      if (!shouldLog('info')) return
      if (import.meta.env.DEV) {
        console.warn(`[INFO][${category}]`, message, data ?? '') // dev-only
      }
    },
    warn(message: string, data?: Record<string, unknown>) {
      if (!shouldLog('warn')) return
      console.warn(`[${category}]`, message, data ?? '')
      buffer.push(createEntry('warn', category, message, data))
      if (buffer.length >= MAX_BUFFER) void flushBuffer()
    },
    error(message: string, data?: Record<string, unknown>) {
      if (!shouldLog('error')) return
      console.error(`[${category}]`, message, data ?? '')
      buffer.push(createEntry('error', category, message, data))
      // Flush immediately on error
      void flushBuffer()
    },
  }
}

/**
 * Global error handler — captures unhandled errors and promise rejections.
 * Call once at app startup.
 */
export function installGlobalErrorHandlers(): void {
  const globalLogger = log('Global')

  window.addEventListener('error', (event: ErrorEvent) => {
    const err = event.error as { name?: string; stack?: string } | null
    globalLogger.error('Unhandled error', {
      error: {
        name: String(err?.name ?? 'Error'),
        message: event.message ?? 'Unknown error',
        stack: String(err?.stack ?? ''),
      },
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    })
  })

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const reason = event.reason as { name?: string; message?: string; stack?: string } | null
    const reasonMessage = reason?.message ?? (typeof event.reason === 'string' ? event.reason : 'Unknown rejection')
    globalLogger.error('Unhandled promise rejection', {
      error: {
        name: String(reason?.name ?? 'UnhandledRejection'),
        message: String(reasonMessage),
        stack: String(reason?.stack ?? ''),
      },
    })
  })
}
